// Support both lowercase and uppercase status values for API compatibility
export type ProjectStatus = 
  | 'ongoing' | 'near_completion' | 'past_completion' | 'archived' | 'draft' | 'completed'
  | 'ONGOING' | 'NEAR_COMPLETION' | 'PAST_COMPLETION' | 'ARCHIVED' | 'DRAFT' | 'COMPLETED';

// Interface for flattened fishbone cause items from the backend API
export interface IFishBoneCause {
  text: string;
  why1?: string;
  why2?: string;
  why3?: string;
  why4?: string;
  why5?: string;
  isPrioritized?: boolean;
}

// Helper function to normalize status strings for display
export const normalizeStatus = (status?: string): string => {
  if (!status) return 'Unknown';
  // Convert to lowercase for case-insensitive comparison
  const lowerStatus = status.toLowerCase();
  
  // Map to proper display format with first letter capitalized
  switch(lowerStatus) {
    case 'ongoing': return 'Ongoing';
    case 'near_completion': return 'Near Completion';
    case 'past_completion': return 'Past Completion';
    case 'archived': return 'Archived';
    case 'draft': return 'Draft';
    case 'completed': return 'Completed';
    default: return status; // Return original if not recognized
  }
};

export interface TestCycle {
  id: string;
  projectId: string;
  startDate: string;
  endDate: string;
  metrics: Record<string, number>;
  comments: string;
}

export type ProjectPriority = 'low' | 'medium' | 'high';

// Community Health Domains
export type CHSDomain = 
  | 'Domain 1: Leadership and Governance'
  | 'Domain 2: Collaboration and Partnership'
  | 'Domain 3: Community Health Policy Standards, Guidelines, and Strategies'
  | 'Domain 4: Community Health Infrastructure'
  | 'Domain 5: Community Health Information System'
  | 'Domain 6: Health Products and Technologies, Supplies and Equipment'
  | 'Domain 7: Transport'
  | 'Domain 8: Transport'
  | 'Domain 9: Community Health Financing'
  | 'Domain 10: Service Delivery'
  | 'Domain 11: CHS Client Satisfaction Index'
  | 'Domain 12: CHS CHW Satisfaction Index';

// Community Health Role types
export type CHSRole = 
  | 'CHA'
  | 'Facility In Charge'
  | 'Health Facility Commitee Chair'
  | 'CHP'
  | 'CHC'
  | 'Youth Member/ Adolescent'
  | 'Community Member'
  | string; // For other roles

export interface Milestone {
  id: string;
  title: string;
  dueDate: string;
  completed: boolean;
}

// Interface for team member in CHS projects
export interface CHSTeamMember {
  fullName: string;
  phoneNumber?: string;
  role: CHSRole;
  customRole?: string; // Used when role is 'Community Member' and needs specification
}

// Decision Matrix for gap prioritization
export interface DecisionMatrixItem {
  gap: string;
  urgency: number; // 1-3 scale
  cost: number; // 1-3 scale
  impact: number; // 1-3 scale
  feasibility: number; // 1-3 scale
  total: number;
  rank?: number;
}

// Root Cause Analysis item
export interface RootCauseItem {
  cause: string;
  text?: string; // Alternative to cause for compatibility
  category?: string;
  importance: number; // 1-3 scale
  control: number; // 1-3 scale
  likelihood: number; // 1-3 scale
  impact: number; // 1-3 scale
  urgency?: number; // 1-3 scale
  cost?: number; // 1-3 scale
  feasibility?: number; // 1-3 scale
  total: number;
  score?: number; // Alternative to total for compatibility
  rank?: number;
}

export interface ActionItem {
  id?: string;
  text?: string; // New format
  action?: string; // Old format
  responsiblePerson: string;
  responsiblePersonType?: 'teamMember' | 'lead';
  byWhen?: string;
  timeline?: string; // Alternative to byWhen
  resources?: string;
  status?: string;
}

export interface Project {
  _id?: string;  // MongoDB ID field
  id?: string;   // Allow both formats for compatibility
  programmeArea: string;
  subcategory: string;
  department: string;
  projectCategory: string;
  projectTitle: string;
  responsiblePerson: string;
  domain?: string;  // Added for TypeScript compatibility
  subDomain?: string;  // Added for TypeScript compatibility

  participants: {
    fullName: string;
    email: string;
    phone: string;
    role: string;
  }[];
  problemStatement: string;
  goalStatement: string;
  indicatorDescription: string;
  useCustomIndicator: boolean;
  indicatorNumerator: string;
  indicatorDenominator: string;
  improvementFrom: number;
  improvementTo: number;
  problemIdentificationTool: 'decisionMatrix' | 'paretoChart' | 'other';
  rootCauseAnalysisTool: 'fishbone' | 'drivers' | 'fiveWhy' | 'processMap' | 'FISHBONE' | 'DRIVERS' | 'FIVE_WHY' | 'PROCESS_MAP';
  fishBoneAnalysis?: import('./project-form').FishBoneAnalysis;
  // Flat fishbone structure from backend
  peopleCauses?: IFishBoneCause[];
  processCauses?: IFishBoneCause[];
  policyCauses?: IFishBoneCause[];
  placeCauses?: IFishBoneCause[];
  provisionCauses?: IFishBoneCause[];
  measurementFrequency: 'WEEKLY' | 'DAILY' | 'MONTHLY' | 'QUARTERLY' | 'BIWEEKLY';
  firstCycleDate: string;
  projectEndDate: string;
  initiativeSource: string;
  status: ProjectStatus;
  createdAt: string;
  updatedAt: string;
  priority?: ProjectPriority;
  progress?: number;
  team?: string[];
  milestones?: Milestone[];
  feedback: string[];
  lastModified: string;
  createdBy: string;
  testCycles: TestCycle[]; // Test cycles associated with the project
  comments: string[];
  resources: string[];
  facility?: string;  // Make optional to handle projects that don't have this field
  county?: string;
  subcounty?: string;
  description: string;
  startDate: string;
  endDate: string;
  selectedKPI?: string;  // Added for TypeScript compatibility
  chsTeamMembers?: CHSTeamMember[];  // Added for TypeScript compatibility
  decisionMatrix?: DecisionMatrixItem[];  // Added for TypeScript compatibility
  displayStatus?: string;  // Added for formatted status display
  
  // New fields for enhanced structure
  rootCauseItems?: RootCauseItem[]; // New format for root causes
  prioritizedRootCauses?: RootCauseItem[]; // Old format for prioritized root causes
  actionItems?: ActionItem[]; // New format for action items
  actionPlanItems?: ActionItem[]; // Old format for action items
}
